import type { DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@renderer/shared/components/icons';
import type { TaskCardViewModel } from '../tasks-selectors';

function isDragBlockedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(target.closest('[data-av-no-drag],a,button,input,select,textarea,[role="button"]'));
}

export function TaskCard({
  task,
  isDragging,
  highlighted,
  onDragStart,
  onDragEnd,
  onDropBefore,
  onFocus,
  onEdit,
  onDelete,
  strings
}: {
  task: TaskCardViewModel;
  isDragging: boolean;
  highlighted: boolean;
  onDragStart: (event: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onDropBefore: (event: DragEvent<HTMLElement>) => void;
  onFocus: (taskId: string) => void;
  onEdit: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  strings: { focus: string; edit: string; delete: string };
}): JSX.Element {
  return (
    <article
      className={['av-task-card', isDragging ? 'is-dragging' : '', highlighted ? 'is-highlighted' : ''].filter(Boolean).join(' ')}
      data-task-id={task.id}
      draggable
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDragStart={(event) => {
        if (isDragBlockedTarget(event.target)) {
          event.preventDefault();
          return;
        }

        onDragStart(event);
      }}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDropBefore(event);
      }}
    >
      <div className="av-task-card__actions" data-av-no-drag>
        <button
          aria-label={strings.focus}
          className="av-task-card__action"
          type="button"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onFocus(task.id);
          }}
        >
          <Icon name="focus" />
        </button>
        <button
          aria-label={strings.edit}
          className="av-task-card__action"
          type="button"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onEdit(task.id);
          }}
        >
          <Icon name="pencil" />
        </button>
        <button
          aria-label={strings.delete}
          className="av-task-card__action av-task-card__action--danger"
          type="button"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onDelete(task.id);
          }}
        >
          <Icon name="trash" />
        </button>
      </div>

      <div className="av-task-card__body">
        <h2 className="av-task-card__title">{task.title}</h2>
        {task.description ? <p className="av-task-card__description">{task.description}</p> : null}
      </div>

      <div className="av-task-card__footer">
        <div className="av-task-card__meta">
          <span>{task.dueLabel}</span>
        </div>

        {task.labels.length > 0 ? (
          <div className="av-task-card__tags">
            {task.labels.map((label) => (
              <span key={label} className="av-type-pill">
                {label}
              </span>
            ))}
          </div>
        ) : null}

        {task.context ? (
          <Link className="av-task-card__context" to={task.context.href} data-av-no-drag>
            <div className="av-task-card__context-icon">
              <Icon name={task.context.icon} />
            </div>
            <span>{task.context.primaryLabel}</span>
          </Link>
        ) : null}
      </div>
    </article>
  );
}
